<template>
    <vue-scroll class="pb-20">
        <div class="page-header">
            <h1 v-if="corporation">Edit Corporation: {{ corporation.name }}</h1>
            <h1 v-else>Create Corporation</h1>
        </div>
        <el-form @submit.prevent="submit()" style="max-width: 700px">
            <el-form-item required label="Name" :class="form.state('name')">
                <el-input
                    v-model="form.name"
                    name="name"
                    v-validate="'required'"
                />
                <form-feedback :form="form" field="name" />
            </el-form-item>
            <el-form-item
                required
                label="Subdomain"
                :class="form.state('slug')"
            >
                <el-input
                    class="el-input--lowercase"
                    v-model="form.slug"
                    name="slug"
                    data-vv-as="subdomain"
                    v-validate="'required|alpha_dash'"
                />
                <form-feedback :form="form" field="slug" />
            </el-form-item>
            <el-form-item required label="Notes" :class="form.state('notes')">
                <el-input
                    type="textarea"
                    :autosize="{ minRows: 3, maxRows: 6 }"
                    v-model="form.notes"
                    name="notes"
                />
                <form-feedback :form="form" field="notes" />
            </el-form-item>
            <el-form-item
                required
                label="Payment Type"
                :class="form.state('payment_type')"
            >
                <br />
                <el-radio-group
                    v-model="form.payment_type"
                    name="payment_type"
                    data-vv-as="payment type"
                    v-validate="'required'"
                >
                    <el-radio :label="PaymentTypes.BOTH_OPTIONS"
                        >Both options available</el-radio
                    >
                    <el-radio :label="PaymentTypes.IMMEDIATE"
                        >Immediate</el-radio
                    >
                    <el-radio :label="PaymentTypes.BILL_TO_ACCOUNT"
                        >Bill to Account</el-radio
                    >
                </el-radio-group>
                <form-feedback :form="form" field="payment_type" />
            </el-form-item>
            <el-form-item>
                <div class="d-inline-block">
                    <el-form-item label="Annual Subscription">
                        <el-input-number
                            :precision="2"
                            :step="1"
                            v-model.number="form.annual_subscription"
                        />
                    </el-form-item>
                </div>
                <div class="d-inline-block ml-10">
                    <el-form-item label="Monthly Subscription">
                        <el-input-number
                            :precision="2"
                            :step="1"
                            v-model.number="form.monthly_subscription"
                        />
                    </el-form-item>
                </div>
                <div class="d-inline-block ml-10">
                    <el-form-item label="Late Fee Amount">
                        <el-input-number
                            :precision="2"
                            :step="1"
                            v-model.number="form.late_fee_amount"
                        />
                    </el-form-item>
                </div>
            </el-form-item>
            <el-form-item :class="form.state('has_ups_account')" class="mb-0">
                <el-switch
                    active-text="Has Own UPS Account"
                    v-model="form.has_ups_account"
                />
                <form-feedback :form="form" field="has_ups_account" />
            </el-form-item>
            <el-form-item
                :class="form.state('disregard_late_invoice')"
                class="mb-0"
            >
                <el-switch
                    active-text="Disregard Late Invoices"
                    v-model="form.disregard_late_invoice"
                />
                <form-feedback :form="form" field="disregard_late_invoice" />
            </el-form-item>
            <el-form-item
                :class="form.state('requires_newsletter_sign_off')"
                class="mb-0"
            >
                <el-switch
                    active-text="Requires PDF Proof Newsletter Signoff"
                    v-model="form.requires_newsletter_sign_off"
                />
                <form-feedback
                    :form="form"
                    field="requires_newsletter_sign_off"
                />
            </el-form-item>
            <el-form-item
                :class="form.state('requires_po_number')"
                class="mb-0"
            >
                <el-switch
                    active-text="PO Number Required"
                    v-model="form.requires_po_number"
                />
                <form-feedback :form="form" field="requires_po_number" />
            </el-form-item>
            <el-form-item :class="form.state('limit_posters')" class="mb-0">
                <el-switch
                    active-text="Limit posters to standard sizes"
                    v-model="form.limit_posters"
                />
                <form-feedback :form="form" field="limit_posters" />
            </el-form-item>
            <el-form-item :class="form.state('limit_flats')" class="mb-0">
                <el-switch
                    active-text="Limit copies to 11x17"
                    v-model="form.limit_flats"
                />
                <form-feedback :form="form" field="limit_flats" />
            </el-form-item>
            <el-form-item :class="form.state('colored_only')" class="mb-0">
                <el-switch
                    active-text="Color products only"
                    v-model="form.colored_only"
                />
                <form-feedback :form="form" field="colored_only" />
            </el-form-item>
            <el-form-item :class="form.state('late_fee')" class="mb-0">
                <el-switch
                    active-text="Late Fee Applies after the 5th"
                    v-model="form.late_fee"
                />
                <form-feedback :form="form" field="late_fee" />
            </el-form-item>
            <el-form-item :class="form.state('filler_visibility')" class="mb-0">
                <el-switch
                    active-text="Show Corporation in Filler Table"
                    v-model="form.filler_visibility"
                />
                <form-feedback :form="form" field="filler_visibility" />
            </el-form-item>
            <edit-logo
                :form="form"
                :with-label="true"
                :corporation="corporation"
            />

            <el-form-item
                :class="form.state('brand_color')"
                label="Website Color"
            >
                <el-color-picker v-model="form.brand_color"></el-color-picker>
                <form-feedback :form="form" field="brand_color" />
            </el-form-item>

            <el-form-item
                required
                label="Street"
                :class="form.state('location.street')"
            >
                <el-input
                    v-validate="'required'"
                    name="location.street"
                    data-vv-as="street"
                    v-model="form.location.street"
                />
                <form-feedback :form="form" field="location.street" />
            </el-form-item>
            <el-form-item
                required
                label="City"
                :class="form.state('location.city')"
            >
                <el-input
                    v-validate="'required'"
                    name="location.city"
                    data-vv-as="city"
                    v-model="form.location.city"
                />
                <form-feedback :form="form" field="location.city" />
            </el-form-item>
            <el-form-item
                required
                label="State"
                :class="form.state('location.state')"
            >
                <state-select
                    v-validate="'required'"
                    name="location.state"
                    data-vv-as="state"
                    v-model="form.location.state"
                />
                <form-feedback :form="form" field="location.state" />
            </el-form-item>
            <el-form-item
                required
                label="Zip"
                :class="form.state('location.zip')"
            >
                <el-input
                    v-validate="'required'"
                    name="location.zip"
                    data-vv-as="zip"
                    v-model="form.location.zip"
                />
                <form-feedback :form="form" field="location.zip" />
            </el-form-item>
            <h3>Discounts</h3>
            <span class="text-small"
                >Enter all discounts as decimals up to 2 places, ex: enter 2% as
                0.02.</span
            >
            <table style="width: 100%">
                <tbody>
                    <tr v-for="(discount, index) in form.discounts">
                        <td>
                            <template v-if="!discount.discount_id">
                                <el-form-item
                                    class="mb-0"
                                    :class="
                                        form.state(`discount.${index}.name`)
                                    "
                                >
                                    <el-input
                                        v-validate="'required'"
                                        data-vv-as="discount name"
                                        :name="`discount.${index}.name`"
                                        placeholder="Discount Name..."
                                        v-model="discount.name"
                                    />
                                    <form-feedback
                                        :form="form"
                                        :field="`discount.${index}.name`"
                                    />
                                </el-form-item>
                            </template>
                            <span v-else>{{ discount.name }}</span>
                        </td>
                        <td style="width: 200px">
                            <el-form-item
                                class="mb-0"
                                :class="form.state(`discount.${index}.value`)"
                            >
                                <el-input
                                    v-validate="'required'"
                                    data-vv-as="discount value"
                                    :name="`discount.${index}.value`"
                                    placeholder="Discount in decimal..."
                                    type="number"
                                    step="0.01"
                                    max="0.99"
                                    min="0.01"
                                    v-model.number="discount.value"
                                />
                                <form-feedback
                                    :form="form"
                                    :field="`discount.${index}.value`"
                                />
                            </el-form-item>
                        </td>
                        <td style="width: 50px">
                            <el-button
                                type="danger"
                                size="mini"
                                icon="mdi mdi-close"
                                title="Remove"
                                @click="removeDiscount(discount)"
                            />
                        </td>
                    </tr>
                    <tr v-if="form.discounts.length">
                        <td class="bold text-right" colspan="2">
                            Total Discount: {{ totalDiscount }}
                        </td>
                    </tr>
                </tbody>
            </table>
            <div class="mt-10">
                <el-select
                    style="width: auto"
                    v-model="discountForm.discount_id"
                >
                    <el-option :value="null" label="New Discount..." />
                    <el-option-group
                        label="Existing Discounts"
                        v-if="availableDiscounts.length"
                    >
                        <el-option
                            :key="index"
                            v-for="(discount, index) in availableDiscounts"
                            :label="discount.name"
                            :value="discount.id"
                        />
                    </el-option-group>
                </el-select>
                <el-button
                    type="primary"
                    icon="mdi mdi-plus"
                    @click="addDiscount()"
                    >Add</el-button
                >
            </div>
            <div class="float-right mt-20">
                <el-button
                    @click="deleteCorporation()"
                    icon="mdi mdi-delete"
                    type="danger"
                    v-if="corporation && can(Permissions.DELETE_CORPORATION)"
                >
                    Delete Corporation
                </el-button>
                <el-button @click="back()">Cancel</el-button>
                <el-button
                    type="primary"
                    :loading="form.busy"
                    icon="mdi mdi-content-save"
                    @click="submit"
                >
                    Save
                    <span v-if="corporation">Changes</span>
                </el-button>
            </div>
            <div class="clearfix"></div>
        </el-form>
    </vue-scroll>
</template>

<script>
import { mapState } from "vuex";
import * as Mutations from "@/store/constants/mutations";
import * as PaymentTypes from "@/constants/payment-types";
import * as Actions from "@/store/constants/actions";
import * as Routes from "@/constants/routes";
import EditLogo from "./EditLogo";
import auth from "@/mixins/auth";
export default {
    mixins: [auth],
    components: {
        EditLogo,
    },
    props: {
        corporation: {
            type: Object,
            required: false,
        },
    },
    data() {
        return {
            PaymentTypes,
            form: new Form({
                name: null,
                slug: null,
                logo: null,
                payment_type: PaymentTypes.BOTH_OPTIONS,
                requires_newsletter_sign_off: false,
                requires_po_number: false,
                limit_flats: false,
                limit_posters: false,
                colored_only: false,
                has_ups_account: false,
                disregard_late_invoice: false,
                notes: null,
                brand_color: null,
                location: {
                    street: null,
                    city: null,
                    state: null,
                    zip: null,
                },
                discounts: [],
                annual_subscription: 0,
                monthly_subscription: 0,
                late_fee_amount: 0,
                late_fee: false,
                filler_visibility: false,
            }),
            discountForm: {
                discount_id: null,
            },
            logo_src: null,
            oldData: null,
        };
    },
    computed: {
        ...mapState(["corporations"]),
        ...mapState({
            discounts: (state) => state.discounts.data,
        }),
        availableDiscounts() {
            return this.discounts.filter((item) => {
                return !_.includes(
                    _.map(this.form.discounts, (i) => i.discount_id),
                    item.id,
                );
            });
        },
        totalDiscount() {
            let discount = _.sumBy(this.form.discounts, (item) =>
                item.value ? item.value : 0,
            );
            if (!discount) {
                return 0;
            }
            return discount.toFixed(2);
        },
        hasChanges() {
            if (!this.oldData) {
                return false;
            }
            return this.oldData !== JSON.stringify(this.getCleanFormData());
        },
    },
    mounted() {
        this.form.setVeeErrors(this.veeErrors);
        if (this.corporation) {
            _.extend(
                this.form,
                _.pick(this.corporation, [
                    "name",
                    "slug",
                    "payment_type",
                    "notes",
                    "requires_newsletter_sign_off",
                    "requires_po_number",
                    "limit_flats",
                    "limit_posters",
                    "colored_only",
                    "has_ups_account",
                    "disregard_late_invoice",
                    "annual_subscription",
                    "monthly_subscription",
                    "brand_color",
                    "late_fee_amount",
                    "late_fee",
                    "filler_visibility",
                ]),
            );
            this.corporation.discounts.data.forEach((item) => {
                this.form.discounts.push(
                    _.pick(item, ["discount_id", "name", "value"]),
                );
            });
            if (this.corporation.location) {
                _.extend(
                    this.form.location,
                    _.pick(this.corporation.location, [
                        "street",
                        "city",
                        "state",
                        "zip",
                    ]),
                );
            }
        }
        this.initOldData();
    },
    methods: {
        initOldData() {
            this.oldData = JSON.stringify(this.getCleanFormData());
        },
        getCleanFormData() {
            let data = _.pick(this.form, [
                "name",
                "slug",
                "payment_type",
                "notes",
                "requires_newsletter_sign_off",
                "requires_po_number",
                "limit_flats",
                "limit_posters",
                "colored_only",
                "has_ups_account",
                "disregard_late_invoice",
                "annual_subscription",
                "monthly_subscription",
                "brand_color",
                "late_fee_amount",
                "late_fee",
                "filler_visibility",
            ]);
            data.discounts = this.form.discounts.map((item) =>
                _.pick(item, ["discount_id", "name", "value"]),
            );
            _.extend(
                data.location,
                _.pick(this.form.location, ["street", "city", "state", "zip"]),
            );
            return data;
        },
        onChangeLogo(e) {
            window.Utils.getFileImageData(e.currentTarget).then((response) => {
                this.logo_src = response;
                this.form.logo = e.target.files[0];
            });
        },
        addDiscount() {
            let discount;
            if (this.discountForm.discount_id) {
                let discount = this.discounts.find(
                    (item) => item.id === this.discountForm.discount_id,
                );
                this.form.discounts.push({
                    discount_id: discount.id,
                    name: discount.name,
                    value: discount.default_value,
                });
            } else {
                discount = null;
                this.form.discounts.push({
                    discount_id: null,
                    name: null,
                    value: null,
                });
            }
            this.discountForm.discount_id = null;
            this.updateScroller();
        },
        removeDiscount(discount) {
            window.Utils.arrayDelete(this.form.discounts, discount);
            this.updateScroller();
        },
        submit() {
            this.$validator.validate().then((valid) => {
                if (!valid) {
                    return;
                }
                if (this.corporation) {
                    window.App.putData(
                        `/web-api/corporation/${this.corporation.id}?include=${this.corporations.meta.include}`,
                        this.form,
                    )
                        .then((response) => {
                            this.$notify.success({
                                message: `Changes have been saved`,
                            });
                            this.$store.commit(
                                Mutations.UPDATE_CORPORATION,
                                response.data,
                            );
                            this.initOldData();
                            this.$router.push({
                                name: Routes.CORPORATIONS,
                            });
                        })
                        .finally(() =>
                            this.$store.dispatch(Actions.FETCH_DISCOUNTS),
                        );
                } else {
                    window.App.postData(
                        `/web-api/corporation?include=${this.corporations.meta.include}`,
                        this.form,
                    )
                        .then((response) => {
                            this.$notify.success({
                                message: `New corporation has been saved.`,
                            });
                            this.$store.commit(
                                Mutations.NEW_CORPORATION,
                                response.data,
                            );
                            this.initOldData();
                            this.$router.push({
                                name: Routes.CORPORATIONS,
                            });
                        })
                        .finally(() =>
                            this.$store.dispatch(Actions.FETCH_DISCOUNTS),
                        );
                }
            });
        },
        back() {
            this.$router.push({
                name: Routes.CORPORATIONS,
            });
        },
        deleteCorporation() {
            this.$confirm("Confirm delete corporation?", "Confirmation", {
                confirmButtonText: "Delete",
                confirmButtonClass: "el-button--danger",
                cancelButtonText: "Cancel",
                type: "warning",
            })
                .then(() => {
                    axios
                        .delete(`/web-api/corporation/${this.corporation.id}`)
                        .then(() => {
                            this.$store.commit(
                                Mutations.DELETE_CORPORATION,
                                this.corporation,
                            );
                            this.$notify.success({
                                message: `Corporation ${this.corporation.name} has been deleted.`,
                            });
                            this.back();
                        })
                        .catch(() => {
                            this.$notify.error({
                                message: `Cannot delete ${this.corporation.name}.`,
                            });
                        });
                })
                .catch(() => {});
        },
    },
};
</script>

<style lang="scss" scoped>
.community-locations {
    .community-locations__item {
        &:not(:first-of-type) {
            margin-top: 1rem;
        }
    }
}

.logo-container {
    width: 150px;
    height: 150px;
    position: relative;
    .remove-logo {
        position: absolute;
        top: 0;
        right: 0;
    }
    .image {
        width: 150px;
        height: 150px;
        background-size: contain;
        background-repeat: no-repeat;
    }
}
</style>
